import type { Editor } from '@tiptap/react';

import { useLazyQuery } from '@apollo/client/react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useEditorState } from '@tiptap/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  normalizeHandle,
  type MessageMentionSuggestionItem,
} from '#components/message-markdown-mention/message-markdown-mention';
import {
  closeMessageMentionMenu,
  INACTIVE_MESSAGE_MENTION_MENU_STATE,
  insertMessageMention,
  MESSAGE_MENTION_MENU_PLUGIN_KEY,
  setMessageMentionMenuItems,
  setMessageMentionMenuSelectedIndex,
} from '#components/message-markdown-mention-menu/message-markdown-mention-menu';
import { SEARCH_USERS_BY_USER_NAME } from '#graphql/queries/search-users-by-user-name';

const MESSAGE_MENTION_MENU_WIDTH = 260;

function MessageMentionMenuButton({
  editor,
  item,
  index,
  active,
  commandRange,
}: {
  editor: Editor;
  item: MessageMentionSuggestionItem;
  index: number;
  active: boolean;
  commandRange: { from: number; to: number };
}) {
  return (
    <Button
      justifyContent="flex-start"
      variant="ghost"
      className="message-markdown-mention-menu__button"
      data-active={active ? 'true' : 'false'}
      onMouseEnter={() => setMessageMentionMenuSelectedIndex(editor, index)}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={() => insertMessageMention(editor, item, commandRange)}
    >
      <Flex direction="column" align="flex-start" gap={0}>
        <Text fontSize="sm" fontWeight="medium">
          {item.label}
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {item.displayName}
        </Text>
      </Flex>
    </Button>
  );
}

export function MessageMarkdownMentionMenuPopover({
  editor,
}: {
  editor: Editor | null;
}) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef(0);
  const [suggestions, setSuggestions] = useState<
    MessageMentionSuggestionItem[]
  >([]);
  const [runUserSearch] = useLazyQuery(SEARCH_USERS_BY_USER_NAME);
  const menuState =
    useEditorState({
      editor,
      selector: ({ editor: currentEditor }) =>
        currentEditor
          ? (MESSAGE_MENTION_MENU_PLUGIN_KEY.getState(currentEditor.state) ??
            INACTIVE_MESSAGE_MENTION_MENU_STATE)
          : INACTIVE_MESSAGE_MENTION_MENU_STATE,
    }) ?? INACTIVE_MESSAGE_MENTION_MENU_STATE;

  const normalizedQuery = useMemo(
    () => normalizeHandle(menuState.query),
    [menuState.query],
  );

  const position = useMemo(() => {
    if (!editor || !menuState.active || !menuState.range) {
      return null;
    }

    try {
      const coords = editor.view.coordsAtPos(menuState.range.to);
      const pad = 8;
      const width = MESSAGE_MENTION_MENU_WIDTH;
      return {
        left: Math.max(
          pad,
          Math.min(coords.left, window.innerWidth - width - pad),
        ),
        top: Math.max(
          pad,
          Math.min(coords.bottom + 4, window.innerHeight - 160 - pad),
        ),
      };
    } catch {
      return null;
    }
  }, [editor, menuState.active, menuState.range]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    setMessageMentionMenuItems(editor, suggestions);
    return () => {
      setMessageMentionMenuItems(editor, []);
    };
  }, [editor, suggestions]);

  useEffect(() => {
    if (!editor || !menuState.active || !menuState.range || !normalizedQuery) {
      setSuggestions([]);
      return;
    }

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;
    setSuggestions([]);
    const timeoutId = window.setTimeout(() => {
      void runUserSearch({
        variables: { query: normalizedQuery },
      })
        .then((result) => {
          if (requestIdRef.current !== currentRequestId) {
            return;
          }

          const rows = result.data?.searchUsersByUserName ?? [];
          setSuggestions(
            rows.map((row) => ({
              id: row.id,
              userId: row.id,
              userName: row.userName,
              displayName: row.displayName,
              label: `@${row.userName}`,
            })),
          );
        })
        .catch(() => {
          if (requestIdRef.current !== currentRequestId) {
            return;
          }

          setSuggestions([]);
        });
    }, 120);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    editor,
    menuState.active,
    menuState.range,
    normalizedQuery,
    runUserSearch,
  ]);

  useEffect(() => {
    if (!editor || !menuState.active) {
      return;
    }

    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (menuRef.current?.contains(target)) {
        return;
      }

      closeMessageMentionMenu(editor);
    };

    document.addEventListener('mousedown', onDocumentMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown, true);
    };
  }, [editor, menuState.active]);

  if (
    !editor ||
    !menuState.active ||
    !menuState.range ||
    suggestions.length === 0 ||
    !position
  ) {
    return null;
  }

  const commandRange = menuState.range;
  const activeIndex = Math.min(menuState.selectedIndex, suggestions.length - 1);

  return createPortal(
    <Box
      ref={menuRef}
      className="message-markdown-mention-menu"
      role="listbox"
      aria-label="Message mentions"
      position="fixed"
      zIndex={101}
      left={`${position.left}px`}
      top={`${position.top}px`}
      minW={`${MESSAGE_MENTION_MENU_WIDTH}px`}
      maxW={`min(${MESSAGE_MENTION_MENU_WIDTH}px, calc(100vw - 16px))`}
      maxH="160px"
      overflowY="auto"
    >
      <Flex direction="column" p={1}>
        {suggestions.map((item, index) => (
          <MessageMentionMenuButton
            key={item.id}
            editor={editor}
            item={item}
            index={index}
            active={index === activeIndex}
            commandRange={commandRange}
          />
        ))}
      </Flex>
    </Box>,
    document.body,
  );
}
